// The ticklist: routes you want to do, grouped by priority. Owner-private —
// RLS returns only your own rows, so there is no "whose list is this" param.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { CalendarDays, Globe, Lock, MoveUp, X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  DELETE_TICK_FOR_ROUTE,
  MY_TICKLIST,
  UPDATE_TICK,
  type MyTicklistResult,
  type TickListEntry,
} from "../lib/queries";
import {
  formatDate,
  formatDistance,
  formatDuration,
  formatElevation,
  TICK_PRIORITIES,
  type TickPriority,
} from "../lib/format";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { ActivityList, EmptyState, ErrorBox, Select, Spinner } from "../components/ui";
import { Button } from "@/components/ui/button";

export function TicklistPage() {
  return (
    <LoginGate action="gateAction.keepTicklist" next="/ticklist">
      <Ticklist />
    </LoginGate>
  );
}

function Ticklist() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["ticklist", personId],
    queryFn: () => gqlRequest<MyTicklistResult>(MY_TICKLIST, { personId }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["ticklist", personId] });
    queryClient.invalidateQueries({ queryKey: ["tick", personId] });
    queryClient.invalidateQueries({ queryKey: ["public-ticklist", personId] });
  };

  const patch = useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown> }) =>
      gqlRequest(UPDATE_TICK, { id: vars.id, patch: vars.patch }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (routeId: string) => gqlRequest(DELETE_TICK_FOR_ROUTE, { personId, routeId }),
    onSuccess: invalidate,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const entries = data?.routeTicks.nodes ?? [];
  const open = entries.filter((t) => !t.doneAt);
  const done = entries.filter((t) => t.doneAt);

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("ticklist.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("ticklist.intro")}</p>
      </div>
      {(patch.error ?? remove.error) != null && <ErrorBox error={patch.error ?? remove.error} />}

      {entries.length === 0 && (
        <EmptyState>
          {t("ticklist.empty")}{" "}
          <Link href="/" className="font-medium text-primary transition-colors hover:underline">
            {t("ticklist.browseMap")}
          </Link>
          .
        </EmptyState>
      )}

      {/* Grouped by priority; the query already sorts PRIORITY_ASC so each
          group is contiguous, but grouping explicitly keeps empty groups out. */}
      {TICK_PRIORITIES.map((priority) => {
        const group = open.filter((t) => t.priority === priority);
        if (group.length === 0) return null;
        return (
          <section key={priority} className="mb-6">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">
              {labelizeT(priority)} <span className="text-muted-foreground">({group.length})</span>
            </h2>
            <ul className="space-y-2">
              {group.map((tick) => (
                <TickRow
                  key={tick.id}
                  tick={tick}
                  busy={patch.isPending || remove.isPending}
                  onPriority={(p) => patch.mutate({ id: tick.id, patch: { priority: p } })}
                  onTogglePublic={() => patch.mutate({ id: tick.id, patch: { isPublic: !tick.isPublic } })}
                  onDone={() => patch.mutate({ id: tick.id, patch: { doneAt: new Date().toISOString() } })}
                  onRemove={() => remove.mutate(tick.route.id)}
                />
              ))}
            </ul>
          </section>
        );
      })}

      {done.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("ticklist.done")} <span className="text-muted-foreground">({done.length})</span>
          </h2>
          <ul className="space-y-2">
            {done.map((tick) => (
              <TickRow
                key={tick.id}
                tick={tick}
                busy={patch.isPending || remove.isPending}
                onPriority={(p) => patch.mutate({ id: tick.id, patch: { priority: p } })}
                onTogglePublic={() => patch.mutate({ id: tick.id, patch: { isPublic: !tick.isPublic } })}
                onDone={() => patch.mutate({ id: tick.id, patch: { doneAt: null } })}
                onRemove={() => remove.mutate(tick.route.id)}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function TickRow({
  tick,
  busy,
  onPriority,
  onTogglePublic,
  onDone,
  onRemove,
}: {
  tick: TickListEntry;
  busy: boolean;
  onPriority: (p: TickPriority) => void;
  onTogglePublic: () => void;
  onDone: () => void;
  onRemove: () => void;
}) {
  const { t, labelizeT } = useT();
  const track = tick.route.routeTracksByRouteId.nodes[0]?.track;
  const isDone = tick.doneAt != null;

  return (
    <li
      className={`rounded-xl border bg-card p-3 text-card-foreground shadow-card transition-all duration-150 hover:border-muted-foreground/30 hover:shadow-card-hover ${
        isDone ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            href={`/route/${tick.route.id}`}
            className={`block font-medium text-foreground transition-colors hover:text-primary ${
              isDone ? "line-through" : ""
            }`}
          >
            {tick.route.name}
          </Link>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
            <ActivityList activities={tick.route.activities} />
            {track?.distanceM ? ` · ${formatDistance(track.distanceM)}` : ""}
            {track?.ascentM ? (
              <>
                {" · "}
                <MoveUp aria-hidden className="size-3" />
                {formatElevation(track.ascentM)}
              </>
            ) : null}
            {tick.route.durationMin ? ` · ${formatDuration(tick.route.durationMin)}` : ""}
          </p>
          {tick.targetSeason && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-primary">
              <CalendarDays aria-hidden className="size-3.5" />
              {tick.targetSeason}
            </p>
          )}
          {tick.note && <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{tick.note}</p>}
          {isDone && <p className="mt-1 text-xs text-muted-foreground">{t("ticklist.doneOn", { date: formatDate(tick.doneAt) })}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Select
            value={tick.priority}
            disabled={busy}
            onChange={(e) => onPriority(e.target.value as TickPriority)}
            className="!w-auto !py-1 !text-xs"
            aria-label={t("tick.priorityAria")}
          >
            {TICK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {labelizeT(p)}
              </option>
            ))}
          </Select>
          <div className="flex gap-1.5 text-xs">
            <Button
              type="button"
              onClick={onTogglePublic}
              disabled={busy}
              title={tick.isPublic ? t("tick.publicTitle") : t("tick.privateTitle")}
              aria-label={tick.isPublic ? t("tick.makePrivate") : t("tick.makePublic")}
              variant="outline"
              size="sm"
              className={
                tick.isPublic
                  ? "border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
                  : "text-muted-foreground"
              }
            >
              {tick.isPublic ? <Globe aria-hidden className="size-3.5" /> : <Lock aria-hidden className="size-3.5" />}
            </Button>
            <Button asChild variant="outline" size="sm" className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10">
              <Link href={`/new?routeId=${tick.route.id}`}>{t("ticklist.log")}</Link>
            </Button>
            <Button type="button" onClick={onDone} disabled={busy} variant="outline" size="sm">
              {isDone ? t("common.undo") : t("common.done")}
            </Button>
            <Button
              type="button"
              onClick={onRemove}
              disabled={busy}
              title={t("tick.removeTitle")}
              aria-label={t("tick.removeTitle")}
              variant="outline"
              size="sm"
              className="hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            >
              <X aria-hidden className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}
